/** In-memory web chat blocks for the mock mode (screens without an API): one visitor already blocked (c407). */
import { BLOCK_CLOSE_REASON } from '../../lib/wcblock'
import { toSummary } from '../../lib/inbox'
import { ApiError } from '../client'
import { getApi } from '../index'
import { MockChatApi } from '../mock'
import type { SystemEvent } from '../types'
import type { BlockBody, WcBlockApi, WebBlock, WebBlockList, WebBlockListItem, WebBlockState } from './types'

const H = 3600 * 1000
const DUR: Record<string, number | null> = { '24h': 24 * H, '7d': 7 * 24 * H, forever: null }
const IP_DUR: Record<string, number> = { '1h': H, '24h': 24 * H }

interface Rec extends WebBlock {
  convId: string
  who: string
}

export class MockWcBlockApi implements WcBlockApi {
  /** The signed-in role in mock mode (tests switch it). */
  role: 'owner' | 'head' | 'staff' = 'owner'
  me = { id: 'ploy', name: 'ปอย' }
  hits: Record<string, number> = { c407: 4, c107: 0 }
  private rows: Rec[] = []
  private seq = 1

  constructor(private readonly now: () => number = () => Date.now()) {
    const t = this.now()
    this.rows.push({
      convId: 'c407', who: 'ผู้เยี่ยมชมเว็บ', id: String(this.seq++), conversation_id: 'c407', status: 'active',
      permanent: false, until: new Date(t + 6 * 24 * H).toISOString(), reason: 'ส่งลิงก์โฆษณาซ้ำ ๆ หลายสิบข้อความ',
      by: { id: 'mint', name: 'มิ้นท์' }, at: new Date(t - 24 * H).toISOString(), ip: null, lifted: null,
    })
  }

  private manager(): boolean {
    return this.role === 'owner' || this.role === 'head'
  }

  private live(r: Rec): boolean {
    return !r.lifted && (r.until === null || Date.parse(r.until) > this.now())
  }

  private view(r: Rec): Rec {
    const status = this.live(r) ? 'active' : r.lifted ? (r.lifted.kind === 'replaced' ? 'replaced' : 'unblocked') : 'expired'
    return { ...r, status }
  }

  private state(convId: string): WebBlockState {
    const mine = this.rows.filter((r) => r.convId === convId).map((r) => this.view(r)).reverse()
    const block = mine.find((r) => r.status === 'active') ?? null
    return {
      block,
      history: mine.filter((r) => r !== block).slice(0, 5),
      limit_hits: { days: 7, count: this.hits[convId] ?? 0, last_at: this.hits[convId] ? new Date(this.now() - 40 * 60000).toISOString() : null },
      ip_known: true,
      ip_masked: this.manager() ? '183.88.x.x' : null,
      can: { block: true, forever: this.manager(), ip: this.manager(), unblock: this.manager() },
    }
  }

  async getState(convId: string): Promise<WebBlockState | null> {
    return structuredClone(this.state(convId))
  }

  async block(convId: string, body: BlockBody): Promise<WebBlockState> {
    if (!(body.duration in DUR)) throw new ApiError(422, 'เลือกระยะเวลาบล็อก: 24 ชั่วโมง, 7 วัน หรือถาวร')
    if (body.duration === 'forever' && !this.manager()) throw new ApiError(403, 'บล็อกถาวรได้เฉพาะผู้จัดการหรือเจ้าของ เลือก 24 ชั่วโมงหรือ 7 วันแทน')
    if (body.ip && !this.manager()) throw new ApiError(403, 'บล็อก IP ได้เฉพาะผู้จัดการหรือเจ้าของ')
    const t = this.now()
    const ms = DUR[body.duration]
    const until = ms === null || ms === undefined ? null : new Date(t + ms).toISOString()
    const running = this.rows.filter((r) => r.convId === convId && this.live(r))
    if (running.length && !this.manager()) {
      const longest = running.some((r) => r.until === null) ? null : Math.max(...running.map((r) => Date.parse(r.until as string)))
      if (longest === null || (until !== null && Date.parse(until) < longest)) {
        throw new ApiError(403, 'ผู้ส่งนี้ถูกบล็อกอยู่แล้ว ผู้จัดการหรือเจ้าของเท่านั้นที่ย่นเวลาหรือยกเลิกได้')
      }
    }
    for (const r of running) r.lifted = { by: this.me, at: new Date(t).toISOString(), note: null, kind: 'replaced' }
    this.rows.push({
      convId, who: 'ผู้เยี่ยมชมเว็บ', id: String(this.seq++), conversation_id: convId, status: 'active', permanent: until === null,
      until, reason: body.reason?.trim() || null, by: this.me, at: new Date(t).toISOString(),
      ip: body.ip ? { masked: '183.88.x.x', until: new Date(t + (IP_DUR[body.ip] ?? H)).toISOString(), active: true } : null,
      lifted: null,
    })
    this.closeInChat(convId, t)
    return this.getState(convId) as Promise<WebBlockState>
  }

  /** Like the API (blocks.py _close_for_block): the block closes the open chat with one line; unblock leaves it closed. */
  private closeInChat(convId: string, t: number): void {
    const chat = getApi()
    if (!(chat instanceof MockChatApi)) return
    const c = chat.db.conversations.get(convId)
    if (!c || c.status !== 'open') return
    c.status = 'closed'
    c.ai_mode = null
    c.ai_handoff = null
    const ev: SystemEvent = {
      kind: 'system', id: `e${++chat.db.seq}`, conversation_id: convId, at: new Date(t).toISOString(), event: 'closed',
      staff: { id: this.me.id, name: this.me.name }, reason: BLOCK_CLOSE_REASON,
    }
    chat.db.threads.get(convId)?.push(ev)
    chat.publish({ type: 'message.created', conversation_id: convId, item: ev, conversation: toSummary(c) })
  }

  async unblock(convId: string, note: string | null = null): Promise<WebBlockState> {
    if (!this.manager()) throw new ApiError(403, 'ผู้จัดการหรือเจ้าของเท่านั้นที่ยกเลิกการบล็อกได้')
    const running = this.rows.filter((r) => r.convId === convId && this.live(r))
    if (!running.length) throw new ApiError(409, 'ผู้ส่งนี้ไม่ได้ถูกบล็อกอยู่แล้ว')
    for (const r of running) r.lifted = { by: this.me, at: new Date(this.now()).toISOString(), note: note?.trim() || null, kind: 'unblock' }
    return this.getState(convId) as Promise<WebBlockState>
  }

  async list(): Promise<WebBlockList> {
    const items: WebBlockListItem[] = this.rows
      .map((r) => this.view(r))
      .reverse()
      .map((r) => ({ ...r, site: 'cleanfitdelivery.com', ip_active: !!r.ip?.active && r.status === 'active' }))
    return structuredClone({
      active: items.filter((i) => i.status === 'active'),
      ended: items.filter((i) => i.status !== 'active'),
      ended_days: 30,
      can_unblock: this.manager(),
    })
  }

  async unblockById(blockId: string, note: string | null = null): Promise<WebBlockList> {
    const row = this.rows.find((r) => r.id === blockId)
    if (!row) throw new ApiError(404, 'ไม่พบการบล็อกนี้')
    await this.unblock(row.convId, note)
    return this.list()
  }
}
